import { composeRefs } from '@radix-ui/react-compose-refs'
import classNames from 'classnames'
import React, { forwardRef, useCallback, useRef } from 'react'

import styles from './chat-input-shell.module.scss'
import { useChatInputShellContext } from './chat-input-shell-context'
import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect'

export type ChatInputShellTextareaElement = HTMLTextAreaElement
export type ChatInputShellTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'children'
>

export const ChatInputShellTextarea = forwardRef<
  ChatInputShellTextareaElement,
  ChatInputShellTextareaProps
>(function ChatInputShellTextarea(props, forwardedRef) {
  const { className, disabled: disabledProp, ...rest } = props
  const { textareaRef, submitRef, busy, disabled: ctxDisabled } = useChatInputShellContext()
  const internalRef = useRef<HTMLTextAreaElement>(null)

  const disabled = disabledProp ?? (busy ? false : ctxDisabled)

  const refreshHeight = useCallback(() => {
    const el = internalRef.current
    if (!el) return
    el.style.height = '0'
    el.style.height = el.scrollHeight + 'px'
  }, [])

  useIsomorphicLayoutEffect(() => {
    const el = internalRef.current
    if (!el) return

    function onEnter(e: KeyboardEvent) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (!busy) {
          submitRef.current?.click()
        }
      }
    }

    const resizeObserver = new ResizeObserver(refreshHeight)
    resizeObserver.observe(el)
    el.addEventListener('keydown', onEnter)
    el.addEventListener('input', refreshHeight)

    return () => {
      resizeObserver.disconnect()
      el.removeEventListener('keydown', onEnter)
      el.removeEventListener('input', refreshHeight)
    }
  }, [busy, refreshHeight, submitRef])

  useIsomorphicLayoutEffect(refreshHeight, [rest.value, rest.defaultValue, refreshHeight])

  return (
    <textarea
      {...rest}
      disabled={disabled}
      className={classNames(styles.textarea, className)}
      ref={composeRefs(forwardedRef, internalRef, textareaRef)}
    />
  )
})
